import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getDataWAuth, putDataWAuth } from "../../../utils/api-service";
import {
  EXTENSION_URI,
  EXTENSION_SEND_TO_CCM,
  EXTENSION_SAVE_BP,
  APP_VIEW,
} from "../../../constants/constant";

export const fetchBPRequests = createAsyncThunk(
  "bpExtension/fetchBPRequests",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getDataWAuth(EXTENSION_URI, { view: APP_VIEW.BP });
      return response.data?.extensionRequestList || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch BP requests");
    }
  }
);

export const sendToCCM = createAsyncThunk(
  "bpExtension/sendToCCM",
  async ({ id, requestStatus }, { rejectWithValue }) => {
    try {
      await putDataWAuth(EXTENSION_SEND_TO_CCM, { id, requestStatus });
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to send to CCM");
    }
  }
);

export const saveBPData = createAsyncThunk(
  "bpExtension/saveBPData",
  async (data, { rejectWithValue }) => {
    try {
      const response = await putDataWAuth(EXTENSION_SAVE_BP, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to save BP data");
    }
  }
);

const initialState = {
  requests: [],
  requestsRef: [],
  loading: false,
  error: null,
  actionLoading: false,
  actionError: null,
};

const bpExtensionSlice = createSlice({
  name: "bpExtension",
  initialState,
  reducers: {
    filterBPRequests: (state, action) => {
      const { country, extensionType, countryCode, otherThanJDWINCountries, referenceContract, serialNos } = action.payload;
      
      if (!state.requestsRef.length) return;
      
      if (country === "JDWIN") {
        state.requests = state.requestsRef.filter((res) =>
          extensionType
            ? res?.country === countryCode && res?.extensionType === extensionType
            : !otherThanJDWINCountries.includes(res?.country)
        );
      } else if (country && countryCode) {
        state.requests = state.requestsRef.filter((res) =>
          extensionType
            ? res?.country === countryCode && res?.extensionType === extensionType
            : res?.country === countryCode
        );
      } else if (extensionType) {
        state.requests = state.requestsRef.filter((res) =>
          res?.extensionType === extensionType
        );
      } else {
        state.requests = state.requestsRef;
      }
      if (referenceContract) {
        state.requests = state.requests.filter((res) =>
          String(res?.referenceContract ?? "").toLowerCase().includes(String(referenceContract).toLowerCase())
        );
      }
      if (serialNos && serialNos.length > 0) {
        state.requests = state.requests.filter((res) => {
          const rowSerial = String(res?.serialNo ?? "").toLowerCase();
          return serialNos.some((sn) => rowSerial.includes(String(sn).toLowerCase()));
        });
      }
    },
    clearBPError: (state) => {
      state.error = null;
    },
    clearBPActionError: (state) => {
      state.actionError = null;
    },
    resetBPExtension: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBPRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBPRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload;
        state.requestsRef = action.payload;
      })
      .addCase(fetchBPRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(sendToCCM.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(sendToCCM.fulfilled, (state, action) => {
        state.actionLoading = false;
        const id = action.payload;
        state.requests = state.requests.filter((req) => req.id !== id);
        state.requestsRef = state.requestsRef.filter((req) => req.id !== id);
      })
      .addCase(sendToCCM.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });

    builder
      .addCase(saveBPData.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(saveBPData.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(saveBPData.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });
  },
});

export const {
  filterBPRequests,
  clearBPError,
  clearBPActionError,
  resetBPExtension,
} = bpExtensionSlice.actions;

export default bpExtensionSlice.reducer;
